import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { DepthChartRandomizer } from '../services/depthChartRandomizer';
import { Player, Position, DepthChartEntry, Sport, Team } from '../types';
import {
  mergeBatterySchedulesIntoProtected,
  batteryConflictsWithDh,
  type BatteryScheduleRow
} from '../utils/batteryScheduleUtils';

// ─── Battery persistence ────────────────────────────────────────────────────
// Only the battery fields (pitch/catch schedules + DH) are persisted.
// Everything else (absent players, protected positions map) is session-only.

interface PersistedBattery {
  protectedPitchingSchedule: BatteryScheduleRow[];
  protectedCatchingSchedule: BatteryScheduleRow[];
  protectedDhPlayerId: string;
}

function batteryStorageKey(id: string) {
  return `field-builder-battery-${id}`;
}

function loadBattery(id: string, fallbackId?: string): Partial<PersistedBattery> {
  if (!id) return {};
  try {
    const raw = localStorage.getItem(batteryStorageKey(id));
    if (raw) return JSON.parse(raw) as PersistedBattery;
    // If game-specific key is empty, seed from the team-wide battery so coaches
    // don't have to re-enter their pitcher/catcher schedule for every game.
    if (fallbackId && fallbackId !== id) {
      const fallbackRaw = localStorage.getItem(batteryStorageKey(fallbackId));
      if (fallbackRaw) return JSON.parse(fallbackRaw) as PersistedBattery;
    }
    return {};
  } catch {
    return {};
  }
}

function saveBattery(id: string, settings: {
  protectedPitchingSchedule: BatteryScheduleRow[];
  protectedCatchingSchedule: BatteryScheduleRow[];
  protectedDhPlayerId: string;
}) {
  if (!id) return;
  try {
    localStorage.setItem(batteryStorageKey(id), JSON.stringify(settings));
  } catch { /* storage unavailable — ignore */ }
}

interface UseDepthChartRandomizerProps {
  players: Player[];
  positions: Position[];
  currentDepthChart: DepthChartEntry[];
  activeTab: string;
  sport: Sport;
  teamId: string;
  team?: Team;
  selectedFormation?: string;
  onRandomizationComplete: (entries: DepthChartEntry[]) => void;
  /** Baseball/softball fielding: UI inning count (e.g. extra innings); falls back to team.innings */
  fieldingInnings?: number;
  /**
   * Override the localStorage key used to persist battery settings.
   * Pass `game-{gameId}` so each game has its own pitcher/catcher schedule
   * independent of the team depth-chart battery. Falls back to `teamId`.
   */
  batteryStorageId?: string;
}

interface RandomizationSettings {
  preserveColors: boolean;
  ensureFairPlayTime: boolean;
  showPreview: boolean;
  protectedPositions: { [inning: number]: { [position: string]: string } };
  standardProtectedPositions: { [position: string]: string }; // For non-baseball sports
  absentPlayerIds: string[];
  /** Baseball fielding: ordered pitcher blocks (player + inning span). */
  protectedPitchingSchedule: BatteryScheduleRow[];
  /** Baseball fielding: ordered catcher blocks. */
  protectedCatchingSchedule: BatteryScheduleRow[];
  /** Baseball fielding: same player at DH for every inning (optional). */
  protectedDhPlayerId: string;
}

export const useDepthChartRandomizer = ({
  players,
  positions,
  currentDepthChart,
  activeTab,
  sport,
  teamId,
  team,
  selectedFormation,
  onRandomizationComplete,
  fieldingInnings,
  batteryStorageId,
}: UseDepthChartRandomizerProps) => {
  // Use caller-supplied ID (e.g. `game-{id}`) or fall back to the team-wide key.
  const effectiveBatteryId = batteryStorageId || teamId;

  const [isRandomizing, setIsRandomizing] = useState(false);
  const [showRandomizeModal, setShowRandomizeModal] = useState(false);
  const [randomizationSettings, setRandomizationSettings] = useState<RandomizationSettings>(() => {
    // For game context: try the game-specific key first, fall back to the team key
    // so the coach's default battery carries over to new games automatically.
    const saved = loadBattery(effectiveBatteryId, batteryStorageId ? teamId : undefined);
    return {
      preserveColors: true,
      ensureFairPlayTime: true,
      showPreview: true,
      protectedPositions: {},
      standardProtectedPositions: {},
      absentPlayerIds: [],
      protectedPitchingSchedule: saved.protectedPitchingSchedule ?? [],
      protectedCatchingSchedule: saved.protectedCatchingSchedule ?? [],
      protectedDhPlayerId: saved.protectedDhPlayerId ?? '',
    };
  });
  const [lastRandomizationStats, setLastRandomizationStats] = useState<any>(null);

  // Persist battery fields whenever they change so they survive page navigation.
  // `effectiveBatteryId` is stable for the lifetime of the hook (game or team).
  useEffect(() => {
    saveBattery(effectiveBatteryId, {
      protectedPitchingSchedule: randomizationSettings.protectedPitchingSchedule,
      protectedCatchingSchedule: randomizationSettings.protectedCatchingSchedule,
      protectedDhPlayerId: randomizationSettings.protectedDhPlayerId,
    });
  }, [
    effectiveBatteryId,
    randomizationSettings.protectedPitchingSchedule,
    randomizationSettings.protectedCatchingSchedule,
    randomizationSettings.protectedDhPlayerId,
  ]);

  /**
   * Validate if randomization is possible
   */
  const canRandomize = useCallback(() => {
    const validation = DepthChartRandomizer.validateRandomizationParams(
      players,
      positions,
      {
        activeTab,
        sport,
        teamId,
        preserveColors: randomizationSettings.preserveColors,
        ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
        selectedFormation
      }
    );

    return validation.isValid;
  }, [players, positions, activeTab, sport, teamId, randomizationSettings, selectedFormation]);

  /**
   * Get preview information for randomization
   */
  const getRandomizationPreview = useCallback(() => {
    if (!canRandomize()) return null;

    const teamInnings = fieldingInnings ?? team?.innings;
    return DepthChartRandomizer.getRandomizationPreview(
      players,
      positions,
      {
        activeTab,
        sport,
        teamId,
        teamInnings,
        absentPlayerIds: randomizationSettings.absentPlayerIds,
        protectedDhPlayerId: randomizationSettings.protectedDhPlayerId,
        selectedFormation
      }
    );
  }, [players, positions, activeTab, sport, teamId, team?.innings, fieldingInnings, randomizationSettings.absentPlayerIds, randomizationSettings.protectedDhPlayerId, canRandomize, selectedFormation]);

  /**
   * Execute randomization
   */
  const executeRandomization = useCallback(async () => {
    if (!canRandomize()) {
      const validation = DepthChartRandomizer.validateRandomizationParams(
        players,
        positions,
        { activeTab, sport, teamId, selectedFormation }
      );
      toast.error(validation.errors[0] || 'Cannot randomize depth chart');
      return;
    }

    setIsRandomizing(true);
    try {
      let result;
      const teamInnings = team?.innings;
      const inningCountForFielding = fieldingInnings ?? team?.innings ?? 7;

      // Special handling for baseball/softball lineup
      if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
        result = DepthChartRandomizer.randomizeLineupOrder(
          players,
          positions,
          currentDepthChart,
          {
            activeTab,
            sport,
            teamId,
            preserveColors: randomizationSettings.preserveColors,
            ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
            absentPlayerIds: randomizationSettings.absentPlayerIds,
            selectedFormation
          }
        );
      } else if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
        const pitchSched = randomizationSettings.protectedPitchingSchedule ?? [];
        const catchSched = randomizationSettings.protectedCatchingSchedule ?? [];
        const mergedProtected = mergeBatterySchedulesIntoProtected(
          randomizationSettings.protectedPositions,
          pitchSched,
          catchSched,
          inningCountForFielding
        );
        let dhId = randomizationSettings.protectedDhPlayerId || '';
        if (dhId && batteryConflictsWithDh(mergedProtected, dhId, inningCountForFielding)) {
          dhId = '';
        }
        // Special handling for baseball/softball fielding with protected positions
        result = DepthChartRandomizer.randomizeDepthChart(
          players,
          positions,
          currentDepthChart,
          {
            activeTab,
            sport,
            teamId,
            preserveColors: randomizationSettings.preserveColors,
            ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
            protectedPositions: mergedProtected,
            protectedDhPlayerId: dhId,
            teamInnings: inningCountForFielding,
            absentPlayerIds: randomizationSettings.absentPlayerIds,
            selectedFormation
          }
        );
      } else {
        // Standard randomization for all other cases with protected positions
        result = DepthChartRandomizer.randomizeDepthChart(
          players,
          positions,
          currentDepthChart,
          {
            activeTab,
            sport,
            teamId,
            preserveColors: randomizationSettings.preserveColors,
            ensureFairPlayTime: randomizationSettings.ensureFairPlayTime,
            standardProtectedPositions: randomizationSettings.standardProtectedPositions,
            absentPlayerIds: randomizationSettings.absentPlayerIds,
            selectedFormation
          }
        );
      }

      // Update the depth chart with randomized entries
      onRandomizationComplete(result.entries);
      
      // Store stats for display
      setLastRandomizationStats(result.statistics);

      // Show success message with stats
      const { playersAssigned, benchedPlayers, fairPlayTimeScore } = result.statistics;
      const absentCount = randomizationSettings.absentPlayerIds.length;
      
      let message = `Built lineup for ${playersAssigned} players`;
      
      if (absentCount > 0) {
        message += ` (${absentCount} absent)`;
      }
      
      if (benchedPlayers > 0) {
        message += `, ${benchedPlayers} on bench each inning`;
      }
      
      if (fairPlayTimeScore !== undefined) {
        message += `. Fair play score: ${fairPlayTimeScore}%`;
      }

      toast.success(message, { duration: 3000 });
      setShowRandomizeModal(false);

    } catch (error) {
      console.error('Randomization error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to randomize depth chart');
    } finally {
      setIsRandomizing(false);
    }
  }, [
    canRandomize,
    players,
    positions,
    currentDepthChart,
    activeTab,
    sport,
    teamId,
    team?.innings,
    fieldingInnings,
    randomizationSettings,
    onRandomizationComplete,
    selectedFormation
  ]);

  /**
   * Quick randomize without modal
   */
  const quickRandomize = useCallback(async () => {
    await executeRandomization();
  }, [executeRandomization]);

  /**
   * Show randomization modal
   */
  const showRandomizationModal = useCallback(() => {
    if (!canRandomize()) {
      const validation = DepthChartRandomizer.validateRandomizationParams(
        players,
        positions,
        { activeTab, sport, teamId, selectedFormation }
      );
      toast.error(validation.errors[0] || 'Cannot randomize depth chart');
      return;
    }
    setShowRandomizeModal(true);
  }, [canRandomize, players, positions, activeTab, sport, teamId, selectedFormation]);

  /**
   * Update randomization settings
   */
  const updateRandomizationSettings = useCallback((settings: Partial<RandomizationSettings>) => {
    setRandomizationSettings(prev => ({ ...prev, ...settings }));
  }, []);

  /**
   * Get randomization button text based on context
   */
  const getRandomizeButtonText = useCallback(() => {
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      return 'Field Builder';
    }
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      return 'Build Batting Order';
    }
    return 'Build Positions';
  }, [sport, activeTab]);

  /**
   * Get randomization description based on context
   */
  const getRandomizeDescription = useCallback(() => {
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      return 'Builds your fielding lineup using player cards. Set your battery first, then Build It to fill the rest.';
    }
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      return 'Builds a batting order based on your roster.';
    }
    return 'Builds positions based on player roles and depth levels.';
  }, [sport, activeTab]);

  /**
   * Get the team innings setting or default
   */
  const getTeamInnings = useCallback(() => {
    return team?.innings || 7; // Default to 7 innings
  }, [team?.innings]);

  return {
    // State
    isRandomizing,
    showRandomizeModal,
    randomizationSettings,
    lastRandomizationStats,
    
    // Computed
    canRandomize: canRandomize(),
    randomizationPreview: getRandomizationPreview(),
    randomizeButtonText: getRandomizeButtonText(),
    randomizeDescription: getRandomizeDescription(),
    teamInnings: getTeamInnings(),
    
    // Actions
    executeRandomization,
    quickRandomize,
    showRandomizationModal,
    updateRandomizationSettings,
    setShowRandomizeModal
  };
}; 